import { memo } from 'react';
import { Helmet } from 'react-helmet-async';
import { yupResolver } from '@hookform/resolvers/yup';

import { excludeKey, setErrors } from '@utils/object';
import { Button, useSnackbar } from '@new-components/elements';
import { FormProvider, useForm } from '@hooks/react-hook-form';
import {
  FormParam,
  useRGetApplicationData,
  useRSaveApplication,
  useRGetSavedApplication,
  SavedApplicationDataResponse,
} from '@repositories/new-application';

import ApplicationCard from '../../application-card';

import { defaultValues, personalIdentitySchema } from './constants';
import { PersonalIdentityProps } from './types';

import {
  SectionIdentityPhoto,
  SectionDomicile,
  SectionPersonalContact,
  SectionPersonalData,
} from './components/sections';

const PersonalIdentityForm = (props: PersonalIdentityProps) => {
  const { setStep } = props;
  const { onOpen } = useSnackbar();

  const form = useForm<FormParam<'personal_identity'>>({
    defaultValues,
    resolver: yupResolver(personalIdentitySchema),
    shouldFocusError: false,
    mode: 'onChange',
  });

  const { handleSubmit, watch, setError, reset } = form;

  const { isLoading: isLoadingGetApplicationData } = useRGetApplicationData({
    page: 1,
  });

  const { mutateAsync: mutateAsyncSaveForm, isLoading: isSavingForm } =
    useRSaveApplication({
      onError: (err) => {
        const statusCode = err.payload?.statusCode;
        const errors = (err.payload?.errors ||
          {}) as FormParam<'personal_identity'>;

        switch (statusCode) {
          case 400:
            setErrors<FormParam<'personal_identity'>>(errors, setError);
            break;

          default:
            onOpen({
              message:
                (err.payload?.errors as Array<string>)?.[0] ||
                'Opps, Something went wrong.',
              severity: 'error',
            });
            break;
        }
      },
    });

  const isPhotosUpload =
    !!Boolean(watch('ktp')) && !!Boolean(watch('selfieKtp'));

  async function onSubmit(fields: FormParam<'personal_identity'>) {
    try {
      await mutateAsyncSaveForm(
        {
          variables: {
            ...excludeKey<SavedApplicationDataResponse<'personal_identity'>>(
              fields as SavedApplicationDataResponse<'personal_identity'>,
              ['ktp', 'selfieKtp', 'currentStep'],
            ),
            step: 1,
          },
        },
        {
          onSuccess: () => {
            setStep((prev) => prev + 1);
          },
        },
      );
    } catch (error) {
      return;
    }
  }

  useRGetSavedApplication<SavedApplicationDataResponse<'personal_identity'>>({
    step: 1,
    onSuccess: ({ data }) => {
      reset(data);
    },
  });

  return (
    <FormProvider scrollToFieldOnError {...form}>
      <Helmet>
        <title>JULO - Form Data Diri </title>
      </Helmet>
      <form onSubmit={handleSubmit(onSubmit)} className='personal-identity'>
        <SectionIdentityPhoto />

        {isPhotosUpload && (
          <>
            <SectionPersonalData />
            <SectionDomicile />
            <SectionPersonalContact />

            <ApplicationCard>
              <Button
                type='submit'
                fullWidth
                disabled={isSavingForm || isLoadingGetApplicationData}
              >
                Lanjutkan
              </Button>
            </ApplicationCard>
          </>
        )}
      </form>
    </FormProvider>
  );
};

export default memo(PersonalIdentityForm);
